import moment from 'moment';
import { SrfLetterboxPlayer } from '@aron/common/SrfLetterboxPlayer';
import {
  FOCUS_MOVE,
  FOCUS_MOVE_FORCE,
  MEDIA_READY,
  MEDIA_FULLSCREEN_CHANGE,
  MEDIA_ERROR,
  MEDIA_PAUSED,
  MEDIA_PLAYING,
  MEDIA_TIME_UPDATE,
  MEDIA_VOLUME_CHANGED,
  onEvent,
  TRACK_INTERACTION,
  TRACK_UDP_ACTION,
  triggerEvent,
} from '@fec/assets/js/utils/event';
import DebounceHelper from '@fec/frontend/foundation/client/debounce-helper';
import ClientStorage from '@fec/frontend/foundation/client/storage';
import { KEYCODES } from '@fec/frontend/foundation/utils/keycodes';
import { getConfig } from '@fec/assets/js/utils/config';
import { Trans } from '@fec/assets/js/utils/trans';

const ELEMENT_HOOK = 'js-radio-live-player';
const LOCAL_CHANNEL_STORAGE_KEY = 'srf:radio-live-player:local-channel';
export const STREAM_MODE_STORAGE_KEY = 'srf:radio-live-player:stream-mode';
const LAST_PLAYED_NAV_STORAGE_KEY = 'srf:radio-live-player:last-played-type';
const CURRENT_TIME_STORAGE_KEY = 'srf:radio-live-player:current-time';
const HIDDEN_CLASS = 'h-element--hide';
const LETTERBOX_CONTAINER_WRAPPER_ID =
  'js-radio-live-player-letterbox-container';
const DISABLED_CLASS = 'radio-live-player__disabled-element';
const LIVE_BUTTON_TIMESHIFT_CLASS =
  'radio-live-player__live-button--time-shift';

const NOW_AND_NEXT_FETCH_INTERVAL = 30 * 1000;
const LAST_PLAYED_FETCH_INTERVAL = 3 * 60 * 1000;
const UPDATE_CLOCK_INTERVAL = 10 * 1000;
const HOURS_OF_PAST_CONTENT = 6;

const TIME_DISPLAY_FORMAT =
  getConfig().BUSINESS_UNIT === 'srf' ? 'HH:mm [Uhr]' : 'HH:mm [Uras]';

const STREAM_MODE_AUDIO = 'audio';
export const STREAM_MODE_VIDEO = 'video';
const NR_OF_INITIAL_LAST_PLAYED_ITEMS = 5;

const REGIONALJOURNAL_GR_URN_EPG =
  'urn:srf:audio:55e3acbd-d643-4491-8e7a-0352581c474c';
const REGIONALJOURNAL_GR_URN_IL =
  'urn:srf:audio:8ba4aff7-1be6-436e-91b4-c6b77c8db88f';

const REGIONALJOURNAL_SRF1_URN =
  'urn:srf:audio:69e8ac16-4327-4af4-b873-fd5cd6e895a7';

const POPOUT_PLAYER_WIDTH = 480;

const CONFIGURATION = {
  controlBar: true, // needs to be true initially, so we are able to use enableControls() / disableControls()
  header: false,
  sharing: false,
  title: false,
  thumbnails: false,
  playButton: false,
  subdivisions: false,
};

export function init() {
  const container = document.querySelector('.js-radio-live-player-container');
  if (container) {
    new RadioLivePlayer(container);
  }
}

export class RadioLivePlayer {
  constructor(container) {
    this.element = container.querySelector(`.${ELEMENT_HOOK}`);
    this.isTimeshift = false;

    this.isPopoutPlayer = this.element.getAttribute('data-is-popout') === '1';
    this.popoutResizeObserver = null;
    this.allowVisualTimeshift =
      this.element.getAttribute('data-allow-visual-timeshift').toLowerCase() ===
      'true';

    this.setupUserInterface(container);
    this.trans = new Trans().getAll(this.element);

    // use a "regionaljournal" urn from local storage if set and the select menu is shown,
    // else get it from the data attribute of the container
    const localChannelUrn = this.getLocalChannelUrnFromStorage();
    if (this.localChannelSelect && localChannelUrn) {
      this.mainStreamUrn = localChannelUrn;
    } else {
      this.mainStreamUrn = this.element.getAttribute('data-main-stream-urn');
    }

    this.updateLocalChannelSelect(this.mainStreamUrn);

    this.visualStreamUrn = this.element.getAttribute('data-visual-stream-urn');

    let initialStreamUrnToShow = this.mainStreamUrn;
    this.streamMode = ClientStorage.getItem(
      STREAM_MODE_STORAGE_KEY,
      STREAM_MODE_AUDIO,
    );

    // if user last watched the visual stream and there is a visual stream,
    // switch to it. If there is no visual stream, make sure the audio mode is
    // chosen
    if (this.visualStreamUrn && this.streamMode === STREAM_MODE_VIDEO) {
      $(this.videoStreamToggle).prop('checked', true);
      this.changeStreamMode(STREAM_MODE_VIDEO, false);
      initialStreamUrnToShow = this.visualStreamUrn;
    } else {
      this.changeStreamMode(STREAM_MODE_AUDIO, false);
    }

    this.player = new SrfLetterboxPlayer({
      containerWrapperId: LETTERBOX_CONTAINER_WRAPPER_ID,
      assetId: initialStreamUrnToShow,
      components: CONFIGURATION,
      shouldSubscribeToTimeline: this.canTimeShift(),
      calculateTimestampOffsetForEachSegment:
        this.streamMode === STREAM_MODE_AUDIO,
    });

    this.setupPlayerEventListeners();
    this.setupUserEventListeners();
    this.setupLiveDataUpdate();
    this.setupHistory();
    this.initPopoutHeightResizeObserver();
  }

  setupUserInterface(container) {
    // set up all references to the UI
    this.playButton = container.querySelector(
      '.js-radio-live-player-play-button',
    );
    this.pauseButton = container.querySelector(
      '.js-radio-live-player-pause-button',
    );
    this.backButton = container.querySelector(
      '.js-radio-live-player-backwards-button',
    );
    this.forwardButton = container.querySelector(
      '.js-radio-live-player-forwards-button',
    );
    this.fullscreenButton = container.querySelector(
      '.js-radio-live-player-fullscreen-button',
    );
    this.popoutButton = container.querySelector(
      '.js-radio-live-player-popout-button',
    );
    this.videoStreamToggle = container.querySelector(
      '.js-toggle-visual-radio .js-toggle-button-input',
    );
    this.volumeContainer = container.querySelector(
      '.js-radio-live-player-volume-container',
    );
    this.volumeButton = container.querySelector(
      '.js-radio-live-player-volume-button',
    );
    this.volumeMutedButton = container.querySelector(
      '.js-radio-live-player-volume-button-muted',
    );
    this.volumeSlider = container.querySelector(
      '.js-radio-live-player-volume-slider',
    );
    this.seekerBar = container.querySelector('.js-radio-live-player-seeker');

    this.endTimeDisplay = container.querySelector(
      '.js-radio-live-player-end-time',
    );
    this.goLiveButton = container.querySelector(
      '.js-radio-live-player-live-button',
    );
    this.localChannelSelect = container.querySelector(
      '.js-radio-live-player-local-select',
    );

    // init UI with data
    this.setTimeToNow();
    this.hasEverPlayed = false;
  }

  setupPlayerEventListeners() {
    const executeIfNeeded = (data, callback = () => {}) => {
      if (
        data.id === LETTERBOX_CONTAINER_WRAPPER_ID &&
        data.assetId === this.player.getCurrentUrn().toLowerCase()
      ) {
        // preserve "this" context
        callback.call(this, data);
      }
    };

    onEvent({
      eventName: MEDIA_PLAYING,
      eventHandler: ({ detail }) =>
        executeIfNeeded(detail, this.onMediaPlaying),
    });

    onEvent({
      eventName: MEDIA_ERROR,
      eventHandler: ({ detail }) => executeIfNeeded(detail, this.onMediaError),
    });

    onEvent({
      eventName: MEDIA_PAUSED,
      eventHandler: ({ detail }) => executeIfNeeded(detail, this.onMediaPaused),
    });

    onEvent({
      eventName: MEDIA_VOLUME_CHANGED,
      eventHandler: ({ detail }) =>
        executeIfNeeded(detail, this.onVolumeChanged),
    });

    onEvent({
      eventName: MEDIA_TIME_UPDATE,
      eventHandler: ({ detail }) => executeIfNeeded(detail, this.onTimeUpdate),
    });

    onEvent({
      eventName: MEDIA_READY,
      eventHandler: () => this.onReady(),
    });

    onEvent({
      eventName: MEDIA_FULLSCREEN_CHANGE,
      eventHandler: () => this.onFullscreenChange(),
    });
  }

  onMediaPlaying() {
    const shouldFocusOnPause = document.activeElement === this.playButton;
    this.hasEverPlayed = true;

    // exchange play/pause button
    this.playButton.classList.add(HIDDEN_CLASS);
    this.pauseButton.classList.remove(HIDDEN_CLASS);

    // focus on pause if the focus was on play before
    if (shouldFocusOnPause) {
      this.pauseButton.focus();
    }

    // make other elements interactive
    this.toggleEnabled(this.volumeContainer, true);
    this.toggleEnabled(this.goLiveButton, true);

    if (this.canTimeShift()) {
      this.toggleEnabled(this.seekerBar.parentNode, true);
    } else {
      this.goLiveButton.classList.remove(LIVE_BUTTON_TIMESHIFT_CLASS);
      this.startClock();
    }
  }

  onMediaError() {
    this.toggleEnabled(this.volumeContainer, false);
    this.toggleEnabled(this.seekerBar.parentNode, false);
    this.toggleEnabled(this.goLiveButton, false);
    this.toggleEnabled(this.backButton, false);
    this.toggleEnabled(this.forwardButton, false);
    this.toggleEnabled(this.playButton, false);
    this.goLiveButton.classList.add(LIVE_BUTTON_TIMESHIFT_CLASS);
    this.playButton.classList.remove(HIDDEN_CLASS);
    this.pauseButton.classList.add(HIDDEN_CLASS);

    // No Timeshift? --> always LIVE --> timer updates itself --> should stop on error
    if (!this.canTimeShift()) {
      this.stopClock();
    }
  }

  onMediaPaused() {
    const shouldFocusOnPlay = document.activeElement === this.pauseButton;
    this.playButton.classList.remove(HIDDEN_CLASS);
    this.pauseButton.classList.add(HIDDEN_CLASS);
    this.goLiveButton.classList.add(LIVE_BUTTON_TIMESHIFT_CLASS);

    // focus on play if the focus was on pause before
    if (shouldFocusOnPlay) {
      this.playButton.focus();
    }

    // No Timeshift? --> always LIVE --> timer updates itself --> should stop on pause
    if (!this.canTimeShift()) {
      this.stopClock();
    }
  }

  onTimeUpdate(data) {
    const { datetime, percentage } = data;

    // in the unlikely case that the current source is no DVR stream, there
    // would be no datetime provided.
    if (datetime) {
      // update the time display (end time)
      const playingTime = moment(datetime).format(TIME_DISPLAY_FORMAT);
      this.endTimeDisplay.textContent = playingTime;

      // Accessibility: value of the slider is 0 to 1000, but we'd rather have a readable time
      this.seekerBar.setAttribute('aria-valuetext', playingTime);

      this.jumpToPreviousPositionIfAvailable();
    }

    // move the slider to the correct position
    this.seekerBar.value = percentage * 10;

    // enable/disable UI elements
    this.toggleEnabled(this.backButton, Math.round(percentage) > 0);
    this.toggleEnabled(this.forwardButton, Math.round(percentage) < 100);
    this.toggleEnabled(this.playButton, true);

    // Go-Live button should be grey if we're not at the leading edge, i.e. live
    this.onLivenessChange(Math.round(percentage) === 100);

    if (datetime) {
      this.updateShowInfoByTime(datetime);
    }
  }

  /**
   * To be able to use the enable/disable function for the controls, the controls must be enabled in the configuration.
   * But we don't need them initially, so we hide them, as soon as we are ready.
   */
  onReady() {
    this.player.disableControls();
  }

  /**
   * In case of fullscreen, the controls need to be enabled and the focus set
   * accordingly.
   */
  onFullscreenChange() {
    if (this.player.letterbox.player.isFullscreen()) {
      this.player.enableControls();

      if (document.fullscreenElement) {
        document.fullscreenElement.focus();
      } else if (document.webkitCurrentFullScreenElement) {
        // safari needs a vendor prefix:
        document.webkitCurrentFullScreenElement.focus();
      }
    } else {
      this.player.disableControls();
      this.fullscreenButton.focus();
      this.startPopoutHeightResizeObserver();
    }
  }

  /**
   * If we're live, the LIVE button should be red, otherwise grey.
   *
   * Additionally, the show info and the presenter are hidden via CSS when not
   * live, as it's confusing when the presenter that is presenting _now_ is
   * shown but the presenter from an hour ago is heard.
   *
   * @param isLive
   */
  onLivenessChange(isLive) {
    this.isTimeshift = !isLive;
    this.element.classList.toggle('radio-live-player--timeshift', !isLive);
    this.goLiveButton.classList.toggle(LIVE_BUTTON_TIMESHIFT_CLASS, !isLive);
  }

  onVolumeChanged(data) {
    // The player handles muted and volume state separately. If the player
    // is muted, the same event is sent, but the volume stays at the
    // previous level (makes it easier to jump back to the previous volume
    // when unmuting). When the muted flag is set, we act as if the volume
    // was set to zero.
    const { value, muted } = data;
    let percentageValue = Math.floor(value * 100);

    if (muted || value === 0) {
      this.volumeButton.classList.add(HIDDEN_CLASS);
      this.volumeMutedButton.classList.remove(HIDDEN_CLASS);
      percentageValue = 0;

      if (this.shouldFocusOnVolumeButton) {
        this.volumeMutedButton.focus();
      }
    } else {
      this.volumeButton.classList.remove(HIDDEN_CLASS);
      this.volumeMutedButton.classList.add(HIDDEN_CLASS);

      if (this.shouldFocusOnVolumeButton) {
        this.volumeButton.focus();
      }
    }

    this.volumeSlider.value = percentageValue;
    this.volumeSlider.setAttribute(
      'aria-valuetext',
      this.trans.volumeLabel.replace('%percentage%', percentageValue),
    );

    this.shouldFocusOnVolumeButton = false;
  }

  setupUserEventListeners() {
    $(this.playButton).on('click', () => this.player.play());
    $(this.pauseButton).on('click', () => this.player.pause());
    $(this.backButton).on('click', () => {
      this.player.skip(-15);
      this.trackInteraction('skip', 'prev');
      this.trackInteractionUDP({
        action_element: '15s_backwards',
        action_name: 'timeshift',
        type: 'ui_event',
      });
    });
    $(this.forwardButton).on('click', () => {
      this.player.skip(30);
      this.trackInteraction('skip', 'next');
      this.trackInteractionUDP({
        action_element: '30s_forward',
        action_name: 'timeshift',
        type: 'ui_event',
      });
    });

    $(this.fullscreenButton).on('click', () => {
      this.stopPopoutHeightResizeObserver();
      this.player.requestFullscreen();
    });

    $(this.popoutButton).on('click', () => this.onPopoutButtonClicked());

    $(this.videoStreamToggle).on('change', () => {
      if ($(this.videoStreamToggle).is(':checked')) {
        this.changeStreamMode(STREAM_MODE_VIDEO);
      } else {
        this.changeStreamMode(STREAM_MODE_AUDIO);
      }
      setTimeout(() => {
        // move the flying focus, usually audio and video windows differ in size
        // hence, we need to adapt to new position of the toggle
        triggerEvent(FOCUS_MOVE);
      }, 250);
    });

    $(this.volumeButton)
      .on('keydown', (e) => {
        if (e.keyCode === KEYCODES.enter) {
          this.shouldFocusOnVolumeButton = true;
        }
      })
      .on('click', () => this.player.setMuted(true));
    $(this.volumeMutedButton)
      .on('keydown', (e) => {
        if (e.keyCode === KEYCODES.enter) {
          this.shouldFocusOnVolumeButton = true;
        }
      })
      .on('click', () => {
        this.player.setMuted(false);
        if (this.player.getVolume() === 0) {
          this.player.setVolume(0.5);
        }
      });

    // Volume Slider
    // Because the behaviour for mouse input vs keyboard input is slightly
    // different, we use 2 event listeners instead of one input listener.
    // For mouse inputs, we remove the focus after a second of no more mouse
    // events, so that the element is hidden again (slider is made visible by
    // :focus-within on the parent element)
    let lastVolume = this.volumeSlider.value;
    let blurTimeout;
    $(this.volumeSlider)
      .on('keyup', () => {
        const val = this.volumeSlider.value;
        if (val !== lastVolume) {
          lastVolume = val;
          this.player.setVolume(val / 100);
        }
      })
      .on('mousemove', () => {
        const val = this.volumeSlider.value;
        if (val !== lastVolume) {
          lastVolume = val;
          this.player.setVolume(val / 100);

          clearTimeout(blurTimeout);
          blurTimeout = setTimeout(() => this.volumeSlider.blur(), 1000);
        }
      });

    $(this.localChannelSelect).on('change', () => {
      const value = $(this.localChannelSelect).val();
      const text = this.localChannelSelect.selectedOptions[0].innerHTML;

      this.trackInteractionUDP({
        action_element: 'dropdown',
        action_name: 'open_regiojournal',
        action_value: text.replaceAll(' ', '_'),
        type: 'ui_event',
      });

      this.changeLocalChannel(value);
    });

    // Safari mobile uses change event (VoiceOver only). Lookup variable prevents events in browsers like Chrome from being called twice.
    const rangeValue = { current: undefined, prev: undefined };
    let startValue = 1000;
    $(this.seekerBar)
      .on('mouseup touchend', (e) => {
        let newValue = parseInt(e.target.value, 10);
        if (startValue > newValue) {
          this.trackInteraction('timeshift', 'back');
        } else if (startValue < newValue) {
          this.trackInteraction('timeshift', 'forward');
        }
        this.trackInteractionUDP({
          action_element: 'slider',
          action_name: 'timeshift',
          type: 'ui_event',
        });
        startValue = newValue;
      })
      .on('input change', (e) => {
        rangeValue.current = e.target.value;
        if (rangeValue.current !== rangeValue.prev) {
          rangeValue.prev = rangeValue.current;

          this.player.goToPercentage(rangeValue.current / 10);
        }
      })
      .on('keydown', (e) => {
        if (e.which === KEYCODES.right) {
          this.player.skip(30);
          this.trackInteraction('timeshift', 'forward');
          e.preventDefault();
        } else if (e.which === KEYCODES.left) {
          this.player.skip(-15);
          this.trackInteraction('timeshift', 'back');
          e.preventDefault();
        }
      })
      .on('mousemove', (e) => {
        const seekerBarWidth = this.seekerBar.clientWidth;
        // it's possible to click & drag the handle seemingly outside of the
        // seeker bar's width - but we only want to display the tooltip there.
        // Solution: clamp the x coordinate of the mouse so it can only be over
        // the seeker bar:
        const xPos = Math.min(Math.max(e.offsetX, 0), seekerBarWidth);

        // Where is the mouse pointer in relation to the width of the bar?
        // all the way to the left? --> 0
        // all the way to the right? --> 1
        // in the middle? --> 0.5
        const hoverLocationRatio = xPos / seekerBarWidth;

        // That means we can subtract (1 - x) times the number of available hours
        // from the current time to get the time to display in the tooltip:
        const startTime = moment().subtract(
          (1 - hoverLocationRatio) * HOURS_OF_PAST_CONTENT,
          'hour',
        );

        this.seekerBar.setAttribute(
          'data-tooltip-value',
          startTime.format(TIME_DISPLAY_FORMAT),
        );
        this.seekerBar.style.setProperty('--tooltip-offset', xPos + 'px');
      });

    $(this.goLiveButton).on('click', () => {
      this.isTimeshift = false;
      this.player.goLive();
      this.trackInteraction('live-button');
      this.trackInteractionUDP({
        action_element: 'live',
        action_name: 'timeshift',
        type: 'ui_event',
      });
    });
  }

  updateLocalChannelSelect(selectedValue) {
    if (this.localChannelSelect) {
      this.localChannelSelect.value = selectedValue;
      let firstItem = this.localChannelSelect.querySelector('option');

      if (firstItem.value === this.localChannelSelect.value) {
        firstItem.innerHTML = 'Regionaljournal wählen';
      } else {
        firstItem.innerHTML = 'Ohne Regionaljournal';
      }
    }
  }

  changeLocalChannel(selectedValue) {
    ClientStorage.setItem(LOCAL_CHANNEL_STORAGE_KEY, selectedValue);
    this.updateLocalChannelSelect(selectedValue);
    this.mainStreamUrn = selectedValue;
    this.player.load(selectedValue);

    // because different regionaljournals can have different shows, we have to re-render the list
    this.renderLastPlayedItems();

    /* TODO extract the following code to a separate function and use it above too */
    const nowAndNextUrl = this.element.dataset.nowAndNextUrl;
    if (nowAndNextUrl) {
      this.fetchNowAndNext(nowAndNextUrl);
    }
  }

  setupLiveDataUpdate() {
    this.currentStationLogoElement = this.element.querySelector(
      '.js-radio-live-player-current-station-logo',
    );
    this.presenterImgElement = this.element.querySelector(
      '.js-radio-live-player-presenter-image',
    );
    this.presenterNameElement = this.element.querySelector(
      '.js-radio-live-player-presenter-name',
    );
    this.presenterLinkElement = this.element.querySelector(
      '.js-radio-live-player-presenter-link',
    );
    this.showTitleElement = this.element.querySelector(
      '.js-radio-live-player-show-title',
    );
    this.showLeadElement = this.element.querySelector(
      '.js-radio-live-player-show-lead',
    );

    const nowAndNextUrl = this.element.dataset.nowAndNextUrl;

    if (!nowAndNextUrl) {
      return;
    }

    this.fetchNowAndNext(nowAndNextUrl);
    setInterval(
      () => this.fetchNowAndNext(nowAndNextUrl),
      NOW_AND_NEXT_FETCH_INTERVAL,
    );

    // make sure that there's never a "broken image" symbol
    this.presenterImgElement.addEventListener('error', () =>
      this.onNoPresenterLogo(),
    );
  }

  /**
   * set an inline image as src, as an empty source is not allowed (data
   * source from https://stackoverflow.com/a/14115340) and hide empty img
   * behind the station logo
   */
  onNoPresenterLogo() {
    this.presenterImgElement.src =
      'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
    this.currentStationLogoElement.classList.add(
      'radio-live-player__current-station-logo--full',
    );
  }

  fetchNowAndNext(url) {
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const { mainPresenter, current = [] } = data;

        let show = null;

        if (current.length === 1) {
          show = current[0];
        } else if (current.length > 1) {
          // there can be multiple shows happening, e.g. with the RegionalJournals
          // find show where the uuid matches the selected local channel
          show = current.find(
            (entry) => entry?.uuid === this.mainStreamUrn.split(':').pop(),
          );
        }

        // if the shown show has a separate presenter, override the mainPresenter
        const presenter = show?.presenter ?? mainPresenter;
        if (presenter) {
          this.updatePresenter(presenter);
        }

        // push the data to the UI
        if (!this.isTimeshift) {
          this.updateShowInfo(show);
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn(error);
      });
  }

  updatePresenter(presenter) {
    const { img, link, firstname, lastname } = presenter;

    if (img) {
      this.currentStationLogoElement.classList.remove(
        'radio-live-player__current-station-logo--full',
      );
      this.presenterImgElement.src = img;
    } else {
      this.onNoPresenterLogo();
    }

    if (firstname && lastname) {
      this.presenterNameElement.textContent = this.trans.presenterText.replace(
        '%moderatorName%',
        `${firstname} ${lastname}`,
      );
      this.presenterNameElement.classList.remove(HIDDEN_CLASS);
    } else {
      this.presenterNameElement.textContent = '';
      this.presenterNameElement.classList.add(HIDDEN_CLASS);
    }

    // if no names are present, we can't fill the title tag of the link; if no
    // image is present, we shouldn't link to the person
    if (img && link && firstname && lastname) {
      this.presenterLinkElement.setAttribute('href', link);
      this.presenterLinkElement.classList.remove(HIDDEN_CLASS);
      this.presenterLinkElement.setAttribute(
        'title',
        this.trans.presenterLinkTitle.replace(
          '%moderatorName%',
          `${firstname} ${lastname}`,
        ),
      );
    } else {
      this.presenterLinkElement.setAttribute('href', '#');
      this.presenterLinkElement.classList.add(HIDDEN_CLASS);
      this.presenterLinkElement.setAttribute('title', '');
    }
  }

  updateShowInfo(show) {
    this.updateShowTitle(show);
    this.updateShowLead(show);
  }

  updateShowInfoByTime(timeshiftTime) {
    if (this.lastPlayedItems.length > 0) {
      let shows = this.lastPlayedItems.filter((item) => {
        return (
          item.type === 'show' &&
          moment(timeshiftTime).isSameOrAfter(moment(item.date)) &&
          moment(timeshiftTime).isSameOrBefore(moment(item.endtime))
        );
      });

      let currentShow = shows[0];

      // special handling for regionaljournal
      if (shows.length > 1) {
        let currentLocalChannelUrn = this.getLocalChannelUrnFromStorage();
        currentShow =
          shows.find(
            (item) => item.regionaljournal === currentLocalChannelUrn,
          ) || null;
      }

      this.updateShowInfo(currentShow);
    }
  }

  updateShowTitle(show) {
    let element = this.showTitleElement;
    let titleElement = null;

    if (show?.title) {
      let showUrl = show.episodeId
        ? `/audio/show/episode?id=${show.episodeId}`
        : null;

      if (showUrl) {
        titleElement = document.createElement('a');
        titleElement.href = showUrl;
        titleElement.setAttribute('target', '_blank');
        titleElement.classList.add('radio-live-player__show-title-link');
      } else {
        titleElement = document.createElement('span');
      }

      let titleTextElement = document.createTextNode(show.title);
      titleElement.appendChild(titleTextElement);

      element.innerHTML = '';
      element.appendChild(titleElement);
      element.classList.remove(HIDDEN_CLASS);
    } else {
      element.innerHTML = '';
      element.classList.add(HIDDEN_CLASS);
    }
  }

  updateShowLead(show) {
    let element = this.showLeadElement;

    // lastPlayed uses subtitle, nowAndNext lead
    let leadText = show?.subtitle ?? show?.lead ?? '';

    if (leadText) {
      element.querySelector('.js-marquee-spacer').textContent = leadText;
      element
        .querySelector('.js-marquee-content')
        .setAttribute('data-content', leadText);
      element.classList.remove(HIDDEN_CLASS);
    } else {
      element.classList.add(HIDDEN_CLASS);
    }
  }

  changeStreamMode(newStreamMode, causedByUser = true) {
    this.streamMode = newStreamMode;

    // Audio-Stream
    if (newStreamMode === STREAM_MODE_AUDIO) {
      this.fullscreenButton?.classList.add(HIDDEN_CLASS);
      this.element.classList.remove('radio-live-player--video-mode');
      this.element.classList.add('radio-live-player--audio-mode');

      this.player?.setOptionsForLiveStreamType(STREAM_MODE_AUDIO);

      if (causedByUser) {
        this.trackInteraction('audio');

        if (this.canTimeShift()) {
          this.changeStreamWithSameStartime(this.mainStreamUrn);
        } else {
          this.player.load(this.mainStreamUrn);
        }

        this.player.subscribeToTimeline();
        this.stopClock();
        this.startUpdatingLastPlayed();
      }
    } // Video-Stream
    else if (newStreamMode === STREAM_MODE_VIDEO) {
      this.fullscreenButton?.classList.remove(HIDDEN_CLASS);
      this.element.classList.remove('radio-live-player--audio-mode');
      this.element.classList.add('radio-live-player--video-mode');

      this.player?.setOptionsForLiveStreamType(STREAM_MODE_VIDEO);

      if (causedByUser) {
        this.trackInteraction('webcam');

        if (this.canTimeShift()) {
          this.changeStreamWithSameStartime(this.visualStreamUrn);
        } else {
          this.player.load(this.visualStreamUrn);
          this.player.unsubscribeFromTimeline();
          this.goLiveButton.classList.remove(LIVE_BUTTON_TIMESHIFT_CLASS);
          this.seekerBar.value = 1000;
          this.toggleEnabled(this.seekerBar.parentNode, false);
          this.toggleEnabled(this.goLiveButton, false);
          this.toggleEnabled(this.backButton, false);
          this.toggleEnabled(this.forwardButton, false);
          this.startClock();
          this.stopUpdatingLastPlayed();
        }
      }
    }

    ClientStorage.setItem(STREAM_MODE_STORAGE_KEY, newStreamMode);
  }

  /**
   * Change stream (from audio to video) with the same start time
   *
   * @param streamUrn
   */
  changeStreamWithSameStartime(streamUrn) {
    // we have to subtract 30 seconds to get closer to the same starttime
    let currentDate = this.player.getCurrentDate();
    let manipulatedDate = moment(currentDate).subtract(30, 'seconds').toDate();
    this.player.load(streamUrn);
    this.player.goToDatetime(manipulatedDate);
  }

  /**
   * Some elements are containers that contain multiple elements where the
   * tabindex has to be removed/set
   *
   * @param element Node
   * @param isEnabled bool
   */
  toggleEnabled(element, isEnabled) {
    const enableNode = (node) => {
      node.removeAttribute('tabindex');
      node.removeAttribute('aria-hidden');
    };
    const disableNode = (node) => {
      node.setAttribute('tabindex', '-1');
      node.setAttribute('aria-hidden', true);
    };

    if (isEnabled) {
      element.classList.remove(DISABLED_CLASS);
      enableNode(element);
      element.querySelectorAll('button, a, input').forEach(enableNode);
    } else {
      element.classList.add(DISABLED_CLASS);
      disableNode(element);
      element.querySelectorAll('button, a, input').forEach(disableNode);
    }
  }

  trackInteraction(eventName, eventValue = null) {
    const trackingData = {
      event_type: 'click',
      event_source: 'liveplayer',
      event_name: eventName,
      event_value: eventValue,
    };
    triggerEvent(TRACK_INTERACTION, trackingData);
  }

  setTimeToNow() {
    const now = moment();
    this.endTimeDisplay.textContent = now.format(TIME_DISPLAY_FORMAT);
  }

  startClock() {
    this.stopClock();
    this.clockInterval = window.setInterval(
      () => this.setTimeToNow(),
      UPDATE_CLOCK_INTERVAL,
    );
    this.setTimeToNow();
  }

  stopClock() {
    if (this.clockInterval) {
      window.clearInterval(this.clockInterval);
    }
  }

  setupHistory() {
    this.lastPlayedUrl = this.element.getAttribute('data-last-played-url');
    // early abort condition: if there's no URL where we can get the last
    // played items from, we can forget about the whole thing
    if (!this.lastPlayedUrl) {
      return;
    }

    // all of these items can be found in _last.played.twig
    const find = (selector) => this.element.querySelector(selector);

    // container around the whole last-played thing
    this.lastPlayedContainer = find('.js-last-played-container');
    // container around the "tab" navigation (to switch between the different types)
    this.lastPlayedNavContainer = find('.js-last-played-nav');
    // the list (<ul>) with the items of the last played songs and/or shows
    this.lastPlayedList = find('.js-last-played-list');
    // template from which the list entries are generated
    this.lastPlayedTemplate = find('.js-last-played-entry-template');
    // container around the show-more-button (needed for some display issues)
    this.loadMoreContainer = find('.js-last-played-show-more-bar-container');
    // the show-more-button which is displayed initially if there are too many list entries to be shown
    this.loadMoreHistoryButton = find('.js-show-more-history');
    // container of the hints at the end of the list which tell the user that only the entries of the last 6 hours can be seen here
    this.historyEndNoticeContainer = find('.js-last-played-notice-container');
    // container of the hints at the end of the list which tell the user that there are no entries for the selected type
    this.noDataNoticeContainer = find(
      '.js-last-played-no-data-notice-container',
    );

    $(document).on('click', '.js-last-played-item-button', (e) => {
      const button = e.currentTarget;
      const dateToJumpTo = button.getAttribute('data-time');
      this.player.goToDatetime(new Date(dateToJumpTo));

      const index = button.getAttribute('data-index');
      this.trackInteractionUDP({
        action_element: 'last_played',
        action_name: 'timeshift',
        action_location: 'last_played',
        action_value: index,
        action_source: 'program',
        type: 'ui_event',
      });
      this.trackInteractionMAPP({
        event_source: 'program',
        event_name: 'timeshift',
      });
    });

    $(document).on('click', '.js-last-played-nav-item-button', (e) => {
      const typeToShow = e.currentTarget.getAttribute(
        'data-last-played-filter',
      );
      this.onLastPlayedTypeChange(typeToShow);
    });

    // if a specific nav item was selected, select it again on load
    if (ClientStorage.hasItem(LAST_PLAYED_NAV_STORAGE_KEY)) {
      this.lastPlayedContainer.setAttribute(
        'data-selected-last-played-filter',
        ClientStorage.getItem(LAST_PLAYED_NAV_STORAGE_KEY),
      );
    }

    this.lastPlayedItems = [];
    this.isShowingAllLastPlayed = false;
    // Click on "Show More" should show all entries and hide itself
    $(this.loadMoreHistoryButton).on('click', () => {
      // set the global flag to TRUE
      this.isShowingAllLastPlayed = true;
      // ... re-render the whole list
      this.renderLastPlayedItems();
      // ... and focus on the first item that was not rendered before (because
      // on click the focus is now on the show-more-button itself which we'll
      // hide right afterwards)
      this.lastPlayedList
        .querySelector(
          `.js-last-played-item:nth-child(${
            NR_OF_INITIAL_LAST_PLAYED_ITEMS + 1
          }) .js-last-played-item-button`,
        )
        .focus();
      // ... and hide the show-more-button's parent element
      this.loadMoreContainer.classList.add(HIDDEN_CLASS);
    });

    if (this.canTimeShift()) {
      this.startUpdatingLastPlayed();
    }
  }

  /**
   * When visiting the page, the history is continually pulled every 3 minutes.
   * But the user can switch to the visual mode, where the history is not
   * displayed, ergo the data doesn't have to be fetched over and over.
   * That's why we do the fetching in an interval and start/stop the interval
   * upon switching modes.
   */
  startUpdatingLastPlayed() {
    if (!this.lastPlayedUrl) {
      return;
    }

    this.stopUpdatingLastPlayed();
    this.updateLastPlayedInterval = window.setInterval(
      () => this.fetchLastPlayedItems(),
      LAST_PLAYED_FETCH_INTERVAL,
    );
    this.fetchLastPlayedItems();
  }

  stopUpdatingLastPlayed() {
    if (!this.lastPlayedUrl) {
      return;
    }

    this.lastPlayedContainer.classList.add(HIDDEN_CLASS);
    if (this.updateLastPlayedInterval) {
      window.clearInterval(this.updateLastPlayedInterval);
    }
  }

  /**
   * - get the played shows and songs from the last 6 hours
   * - save the data
   * - start the rendering process
   * - hide the whole element if something goes wrong
   */
  fetchLastPlayedItems() {
    fetch(this.lastPlayedUrl)
      .then((response) => response.json())
      .then((lastPlayedList) => {
        const lastPlayedArray = lastPlayedList.lastPlayedList;

        this.lastPlayedItems = lastPlayedArray.map(
          ({
            time,
            timestamp,
            title,
            description,
            type,
            songDuringRegionaljournal,
            regionaljournal = '',
            timestampEnd = null,
          }) => ({
            date: timestamp,
            visibleTime: time,
            endtime: timestampEnd,
            title: title,
            subtitle: description,
            type: type,
            songDuringRegionaljournal: songDuringRegionaljournal,
            regionaljournal:
              regionaljournal === REGIONALJOURNAL_GR_URN_EPG
                ? REGIONALJOURNAL_GR_URN_IL
                : regionaljournal,
          }),
        );

        this.renderLastPlayedItems();
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn(e);
        // something went wrong? hide the whole last played element
        this.lastPlayedContainer.classList.add(HIDDEN_CLASS);
      });
  }

  /**
   * @param {('all'|'show'|'song')} typeToShow
   */
  onLastPlayedTypeChange(typeToShow) {
    this.lastPlayedContainer.setAttribute(
      'data-selected-last-played-filter',
      typeToShow,
    );

    // save setting to localstorage so it will be loaded on next reload/page change
    ClientStorage.setItem(LAST_PLAYED_NAV_STORAGE_KEY, typeToShow);

    this.renderLastPlayedItems();
  }

  /**
   * This method generates DOM from a POJO list (this.lastPlayedItems)
   * This is unfortunately depending on a couple of other items and also causing
   * some other elements to be shown/hidden.
   *
   * Some special cases:
   * - there is only 1 song but 50 shows - when showing shows or all elements,
   *   the show-more-button has to be shown (if it hasn't been pressed
   *   before), but when showing only songs the show-more-button is not
   *   necessary and should be hidden
   * - there are no shows but some songs - when showing shows, there is a note
   *   telling the user that there is no data available. The tab navigations
   *   stays visible so the UI doesn't move around
   */
  renderLastPlayedItems() {
    const selectedItemType = this.lastPlayedContainer.getAttribute(
      'data-selected-last-played-filter',
    );
    let showLoadMoreBar = !this.isShowingAllLastPlayed;
    let currentLocalChannelUrn = this.getLocalChannelUrnFromStorage();

    // only show items that correspond to the selected type (song, show, all)
    // and - if it's a local channel - to the selected localChannel
    let itemsToShow = this.lastPlayedItems.filter((item) => {
      const typeFits =
        selectedItemType === 'all' || selectedItemType === item.type;

      if (!typeFits) {
        return false;
      }

      // if we're currently on a regionaljournal and the item is regionaljournal-specific, we have to make sure to only show it if it's the correct one
      if (
        currentLocalChannelUrn &&
        item.regionaljournal &&
        currentLocalChannelUrn !== REGIONALJOURNAL_SRF1_URN
      ) {
        const regioJournalFits =
          item.regionaljournal === currentLocalChannelUrn;

        if (!regioJournalFits) {
          return false;
        }
      }

      return true;
    });

    // if the show-more-button has not been clicked yet, we still have to check
    // whether we will show enough items to warrant the button.
    if (!this.isShowingAllLastPlayed) {
      showLoadMoreBar = itemsToShow.length > NR_OF_INITIAL_LAST_PLAYED_ITEMS;
      itemsToShow = itemsToShow.slice(0, NR_OF_INITIAL_LAST_PLAYED_ITEMS);
    }

    // show the whole element if there is 1 or more items
    this.lastPlayedContainer.classList.toggle(
      HIDDEN_CLASS,
      this.lastPlayedItems.length === 0,
    );
    this.noDataNoticeContainer.classList.toggle(
      HIDDEN_CLASS,
      itemsToShow.length > 0,
    );

    this.loadMoreContainer.classList.toggle(HIDDEN_CLASS, !showLoadMoreBar);

    // show the end-of-list notice ("Only the X from the last 6 hours are
    // shown") if the show-more-button was pressed and if there are any items
    // shown. If there are no items, the other notice ("sorry there's nothing
    // here") will be shown.
    this.historyEndNoticeContainer.classList.toggle(
      HIDDEN_CLASS,
      showLoadMoreBar || itemsToShow.length === 0,
    );

    // if the user's focus was on a list item, make sure the focus is restored after the update!
    let previouslyFocussedItemDateTime = null;
    let lastItemInList = null;
    let elementToFocusAfterUpdate = null;
    if (
      document.activeElement?.classList.contains('js-last-played-item-button')
    ) {
      previouslyFocussedItemDateTime =
        document.activeElement.getAttribute('data-time');
    }

    // empty the <ul> before putting new stuff in
    this.lastPlayedList.textContent = '';

    // fill the <ul> with <li> elements that we'll create here
    itemsToShow.forEach((item, index) => {
      const listEntry =
        this.lastPlayedTemplate.content.firstElementChild.cloneNode(true);
      listEntry.querySelector('.js-last-played-time').textContent =
        item.visibleTime;
      listEntry.querySelector('.js-last-played-title').textContent = item.title;
      listEntry.querySelector('.js-last-played-subtitle').textContent =
        item.subtitle;
      listEntry.setAttribute('data-last-played-item-type', item.type);

      const button = listEntry.querySelector('.js-last-played-item-button');
      button.setAttribute('data-time', item.date);
      button.setAttribute('data-index', (index + 1).toString(10));

      /**
       * Case 1: If no regionaljournal is selected > the regionaljournals are not playable
       * Case 2: If a regionaljournal is selected > the songs during regionaljournal are not playable
       */
      const regionaljournalSelected =
        currentLocalChannelUrn !== null &&
        currentLocalChannelUrn !== REGIONALJOURNAL_SRF1_URN;

      if (
        (!regionaljournalSelected && item.regionaljournal) ||
        (regionaljournalSelected && item.songDuringRegionaljournal)
      ) {
        button.setAttribute('disabled', 'true');
        button.querySelector('.js-last-played-icon').remove();
        button.querySelector('.js-last-played-offscreen').remove();
      }

      if (previouslyFocussedItemDateTime === item.date) {
        elementToFocusAfterUpdate = button;
      }

      lastItemInList = listEntry;
      this.lastPlayedList.appendChild(listEntry);
    });

    // after the list has been updated, let's check if we have to set the focus again
    if (elementToFocusAfterUpdate) {
      // yup, the user was focussed on an item, let's re-focus on that
      elementToFocusAfterUpdate.focus();
      // move the flying focus to the new position after scrolling
      triggerEvent(FOCUS_MOVE_FORCE);
    } else if (previouslyFocussedItemDateTime) {
      // uh-oh, the focus was on an item, but that item doesn't exist anymore
      // focus on the last visible item's button instead
      lastItemInList.querySelector('.js-last-played-item-button').focus();
      // move the flying focus to the new position after scrolling
      triggerEvent(FOCUS_MOVE_FORCE);
    }
    // else there was no focus, so we don't have to re-focus
  }

  /**
   * When opening the popout player, the current player should be paused and a
   * new window opened where one can continue to listen.
   */
  onPopoutButtonClicked() {
    this.player.pause();

    // only save the current time for the popout if timeshift is supported and
    // the medium was played - otherwise start at the LIVE edge in the popout!
    if (this.canTimeShift() && this.hasEverPlayed) {
      const currentDate = this.player.getCurrentDate();

      if (currentDate) {
        ClientStorage.setItemJsonStringified(
          CURRENT_TIME_STORAGE_KEY,
          currentDate,
        );
      }
    }
  }

  /**
   * upon first playing, check if there is a starting time in local storage.
   * If so, delete it and jump to the provided starting time if the
   * circumstances allow it.
   */
  jumpToPreviousPositionIfAvailable() {
    const startTime = ClientStorage.getItemJsonParsed(
      CURRENT_TIME_STORAGE_KEY,
      false,
    );

    if (startTime && this.isPopoutPlayer && this.canTimeShift()) {
      this.player.goToDatetime(new Date(startTime));
      ClientStorage.removeItem(CURRENT_TIME_STORAGE_KEY);
    }
  }

  canTimeShift() {
    return this.streamMode === STREAM_MODE_AUDIO || this.allowVisualTimeshift;
  }

  trackInteractionUDP(values = {}) {
    const payload = Object.assign(
      {
        action_source: 'radio',
        action_type: 'click',
        action_element: null,
        action_name: null,
        action_location:
          this.streamMode === STREAM_MODE_AUDIO
            ? 'radio_player'
            : 'visual_radio',
      },
      values,
    );

    triggerEvent(TRACK_UDP_ACTION, payload);
  }

  trackInteractionMAPP(values = {}) {
    const payload = Object.assign(
      {
        event_source: 'radio',
        event_name: 'hidden_event',
      },
      values,
    );

    triggerEvent(TRACK_INTERACTION, payload);
  }

  initPopoutHeightResizeObserver() {
    if (!this.isPopoutPlayer) {
      return;
    }

    // getting the height of the browser's chrome (header, location bar, borders etc.)
    const browserUIHeight = window.outerHeight - window.innerHeight;

    this.popoutResizeObserver = new ResizeObserver((entries) =>
      DebounceHelper.throttle(
        this.popoutResizeObserverHandler(entries, browserUIHeight),
        100,
      ),
    );
    this.startPopoutHeightResizeObserver();
  }

  startPopoutHeightResizeObserver() {
    this.popoutResizeObserver?.observe(
      window.document.querySelector('.radio-live-player'),
    );
  }

  stopPopoutHeightResizeObserver() {
    this.popoutResizeObserver?.unobserve(
      window.document.querySelector('.radio-live-player'),
    );
  }

  popoutResizeObserverHandler(entries, browserUIHeight) {
    // if a user resizes the window manually (window width changes) we are stopping the observer
    if (window.outerWidth !== POPOUT_PLAYER_WIDTH) {
      this.stopPopoutHeightResizeObserver();
      return;
    }

    entries.forEach((entry) => {
      window.resizeTo(
        POPOUT_PLAYER_WIDTH,
        entry.contentRect.height + browserUIHeight,
      );
    });
  }

  getLocalChannelUrnFromStorage() {
    const savedUrn = ClientStorage.getItem(LOCAL_CHANNEL_STORAGE_KEY);

    if (savedUrn) {
      return savedUrn.toString().toLowerCase();
    } else {
      return null;
    }
  }
}
